import { z } from "zod";
import type { Job, JobStatus } from "./core";

// Routing: one tech's ordered day, live tech positions, and the per-tenant
// settings the optimizer needs. Ported from courierHop (same developer) —
// Google Route Optimization API, self-replanning routes, on-time flags.

export const RoutingSettingsSchema = z.object({
  homeAddress: z.string().max(300).default(""),
  homeLat: z.number().nullable().default(null),
  homeLng: z.number().nullable().default(null),
  timezone: z.string().max(60).default("America/New_York"),
  onTimeToleranceMin: z.number().int().min(0).max(240).default(15),
  defaultServiceMinutes: z.number().int().min(1).max(600).default(30),
  updatedAt: z.number(),
});
export type RoutingSettings = z.infer<typeof RoutingSettingsSchema>;

// Written by the optimizer only. Doc id: `${date}_${techUid}` (date = YYYY-MM-DD
// in the tenant's routing timezone).
export const RouteDocSchema = z.object({
  date: z.string(),
  techUid: z.string(),
  startLabel: z.string().default(""),
  startLat: z.number(),
  startLng: z.number(),
  startAtMs: z.number(),
  jobCount: z.number().int().nonnegative(),
  skippedJobIds: z.array(z.string()).default([]),
  distanceMeters: z.number().nonnegative().default(0),
  durationSeconds: z.number().nonnegative().default(0),
  status: z.enum(["planned", "done"]).default("planned"),
  optimizedAt: z.number(),
  replanningAt: z.number().nullable().default(null),
  replannedAt: z.number().nullable().default(null),
});
export type RouteDoc = z.infer<typeof RouteDocSchema>;

// One doc per tech, overwritten in place — never a growing trail.
export const TechLocationSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  accuracy: z.number().nullable().default(null),
  heading: z.number().nullable().default(null),
  speedMps: z.number().nullable().default(null),
  onDuty: z.boolean().default(false),
  updatedAt: z.number(),
});
export type TechLocation = z.infer<typeof TechLocationSchema>;

// ---- On-time flags (shared by the board, the dashboard and functions) ----

export type JobFlag = JobStatus | "late" | "atRisk" | "doneLate";

export const JOB_FLAG_LABEL: Record<JobFlag, string> = {
  scheduled: "Scheduled",
  in_progress: "On the way",
  done: "Done",
  doneLate: "Done late",
  canceled: "Canceled",
  late: "Behind",
  atRisk: "At risk",
};

/**
 * A job's board flag: its status, sharpened by how it's tracking against its
 * appointment. "Behind" = the end of the appointment window (plus tolerance)
 * has passed; "At risk" = the planned ETA falls after that deadline.
 */
export function jobFlag(
  job: Pick<Job, "status" | "startAt" | "endAt"> & { plannedEtaAt?: number | null; doneAt?: number | null },
  toleranceMin: number,
  nowMs: number = Date.now(),
): JobFlag {
  const deadline = job.endAt !== null ? job.endAt + toleranceMin * 60_000 : null;
  if (job.status === "done") {
    return deadline !== null && job.doneAt && job.doneAt > deadline ? "doneLate" : "done";
  }
  if (job.status === "canceled") return "canceled";
  if (deadline === null) return job.status;
  if (nowMs > deadline) return "late";
  if (job.plannedEtaAt && job.plannedEtaAt > deadline) return "atRisk";
  return job.status;
}

/** One answer from the address lookup (lookupAddress). */
export interface AddressMatch {
  /** "1234 W Desert Sky Ln, Phoenix, AZ 85001" */
  address: string;
  lat: number;
  lng: number;
  /** Google knows this exact address (not just the street or the town). */
  exact: boolean;
  street: string;
  city: string;
  state: string;
  zip: string;
}
